import { CheckBoxOutlineBlankOutlined, CheckBoxOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { Checkbox, FormControlLabel, IconButton, Tooltip } from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import { useAuth } from "hooks";
import moment from "moment";
import React from 'react'
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { useLazyListTransshipmentQuery } from "store/services/transhipmentApi";
import { localizeDateShort } from "utils/dateFormats";
import { exportExcelFile } from "utils/exportExcelFile";

const EXCEL_DATA_INITIAL_STATE = {
    dateRange: [],
    shipmentId: null,
};
const ExportTransshipmentExcel = ({ isBusy = false }) => {

    const [showDialog, setShowDialog] = React.useState(false);
    const [showForm, setShowForm] = React.useState(false);

    const { viewEntity } = useAuth();

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: EXCEL_DATA_INITIAL_STATE,
    });

    // RTK Query implementation
    const [fetchTransshipments] = useLazyListTransshipmentQuery();
    React.useEffect(() => {
        fetchTransshipments({});
    }, [fetchTransshipments]);

    const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
        useLazyGetDDLShipmentListQuery();

    React.useEffect(() => {
        fetchShipments({
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
        });
    }, [fetchShipments, viewEntity?.entity?._id, viewEntity?.entity?.account?._id,]);

    const handleDownload = async (data) => {
        const { dateRange } = data;

        const fromDate = moment(dateRange[0]).format("MM-DD-YYYY");
        const toDate = moment(dateRange[1]).format("MM-DD-YYYY");

        const payload = {
            shipmentId: data?.shipmentId?._id,
            fromDate,
            toDate,
        };

        try {
            const responseDate = await fetchTransshipments(payload).unwrap();

            if (!responseDate?.data || responseDate.data.length === 0) {
                toast.info("No transhipment records found for the selected date range and shipment Id.");
                return;
            }
            const formattedData = responseDate?.data.map((row, index) => ({
                SN: index + 1,
                "Loading ID": row.loadingId || "N/A",
                "Shipment ID": row?.shipmentId?.shipmentId || "N/A",
                "Loading Time": localizeDateShort(row.loadingTime) || "N/A",
                "Unloading Time": localizeDateShort(row.unloadingTime) || "N/A",
                "Loading Location (Lat, Lon)": `${row?.loadingLocation?.lat || "N/A"}, ${row?.loadingLocation?.lon || "N/A"}`,
                "Unloading Location (Lat, Lon)": `${row?.unloadingLocation?.lat || "N/A"}, ${row?.unloadingLocation?.lon || "N/A"}`,
                "Cargo Type": row.cargoType || "N/A",
                "Cargo Condition": row.cargoCondition || "N/A",
            }));

            exportExcelFile({ sheetData: formattedData, fileName: "Transhipment_List" });
            toast.success("data is downloaded")
        } catch (error) {
            toast.info("data is not found", error)
        }
        handleReset();
    };

    // methods
    const handleReset = () => {
        setShowDialog(false);
        reset(EXCEL_DATA_INITIAL_STATE);
    };

    return (
        <>
            <Tooltip title="Download Excel" arrow>
                <IconButton color="success" onClick={() => setShowDialog(true)}>
                    <FileDownloadOutlined />
                </IconButton>
            </Tooltip>

            <AppConfirmDialog
                open={showDialog}
                onClose={handleReset}
                onConfirm={handleSubmit(handleDownload)}
                title="Download Transhipment Excel"
                dialogIcon={() => <FileDownloadOutlined color="success" />}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showForm}
                            onChange={(e) => setShowForm(e.target.checked)}
                            icon={<CheckBoxOutlineBlankOutlined />}
                            checkedIcon={<CheckBoxOutlined />}
                        />
                    }
                    label="Download related shipment Id"
                />
                {showForm && (
                    <AppForm loading={isBusy}>
                        <AppForm.Content>
                            <Controller
                                name="shipmentId"
                                control={control}
                                rules={{ required: "Please select Shipment ID." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        label="Shipment  ID"
                                        required
                                        value={value ?? ""}
                                        onChange={(_, data) => {
                                            onChange(data);

                                        }}
                                        getOptionLabel={(option) => option?.shipmentId ?? ""}
                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                        options={shipments || []}
                                        loading={loadingShipments}
                                        loadingText="Loading Vehicles..."
                                        helperText={errors?.shipmentId?.message}
                                        error={!!errors?.shipmentId}
                                    />
                                )}
                            />
                        </AppForm.Content>
                    </AppForm>
                )}
                {/* Date Range Selection */}
                <Controller
                    name="dateRange"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <DateRangeToggle {...field} label="Select Date Range" />
                    )}
                />
            </AppConfirmDialog>
        </>
    );
};

export default ExportTransshipmentExcel